import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    className?: string;
    loading?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
                                                   currentPage,
                                                   totalItems,
                                                   itemsPerPage,
                                                   onPageChange,
                                                   className = '',
                                                   loading = false
                                               }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startResult = (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalItems);

    // Generate page numbers to show
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 7;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            let startPage = Math.max(1, currentPage - 3);
            let endPage = Math.min(totalPages, currentPage + 3);

            if (currentPage <= 4) {
                startPage = 1;
                endPage = maxVisible;
            }

            if (currentPage >= totalPages - 3) {
                startPage = totalPages - maxVisible + 1;
                endPage = totalPages;
            }

            if (startPage > 1) {
                pages.push(1);
                if (startPage > 2) {
                    pages.push('...');
                }
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pages.push('...');
                }
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    const PageButton: React.FC<{
        page: number | string;
        isActive?: boolean;
        disabled?: boolean;
        onClick?: () => void;
    }> = ({ page, isActive = false, disabled = false, onClick }) => {
        if (page === '...') {
            return (
                <span className="px-3 py-2 text-text-muted-light dark:text-text-muted-dark">
                    ...
                </span>
            );
        }

        return (
            <button
                onClick={onClick}
                disabled={disabled || loading}
                className={`
                    px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                    ${isActive
                    ? 'bg-primary-500 text-white shadow-md'
                    : 'text-text-primary-light dark:text-text-primary-dark hover:bg-background-light-elevated dark:hover:bg-background-dark-elevated'
                }
                    ${disabled || loading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:shadow-sm active:scale-95'
                }
                `}
            >
                {page}
            </button>
        );
    };

    const NavigationButton: React.FC<{
        icon: React.ReactNode;
        onClick: () => void;
        disabled?: boolean;
        label: string;
    }> = ({ icon, onClick, disabled = false, label }) => (
        <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`
                p-2 rounded-lg transition-all duration-200 flex items-center justify-center
                ${disabled || loading
                ? 'opacity-50 cursor-not-allowed'
                : 'text-text-primary-light dark:text-text-primary-dark hover:bg-background-light-elevated dark:hover:bg-background-dark-elevated hover:shadow-sm active:scale-95'
            }
            `}
            title={label}
        >
            {icon}
        </button>
    );

    if (totalItems === 0) {
        return null;
    }

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
            {/* Results Info */}
            <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Showing {startResult.toLocaleString()} to {endResult.toLocaleString()} of{' '}
                <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                    {totalItems.toLocaleString()}
                </span>{' '}
                results
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1">
                <NavigationButton
                    icon={<ChevronsLeft className="h-4 w-4" />}
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    label="First page"
                />

                <NavigationButton
                    icon={<ChevronLeft className="h-4 w-4" />}
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    label="Previous page"
                />

                <div className="flex items-center gap-1 mx-2">
                    {pageNumbers.map((page, index) => (
                        <PageButton
                            key={`${page}-${index}`}
                            page={page}
                            isActive={page === currentPage}
                            onClick={() => typeof page === 'number' && onPageChange(page)}
                        />
                    ))}
                </div>

                <NavigationButton
                    icon={<ChevronRight className="h-4 w-4" />}
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    label="Next page"
                />

                <NavigationButton
                    icon={<ChevronsRight className="h-4 w-4" />}
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    label="Last page"
                />
            </div>
        </div>
    );
};

export default Pagination;